import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import type { PageEntityConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { useCommonTranslation } from 'c-translation';
import type { Campaign } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import CampaignOverviewPopShotsParentTab from 'c-main/Components/Campaign/CampaignOverviewPopShotsTab/CampaignOverviewPopShotsParentTab';
import { useEntityData } from 'c-data';
import { useEntityPage } from 'c-hooks';
import Sidebar from 'c-main/Pages/Campaign/CampaignOverviewPage/SideBar';
import { Menu as MenuIcon } from '@mui/icons-material';
import ReactGA from 'react-ga';
import type { SystemSearchEntityResource } from 'c-system-search';
import { campaignIcon } from 'c-main/icons';

const CampaignPopImagesPage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);
    const { id } = useEntityPage();
    const { getById } = useEntityData<Campaign>('Campaign');
    const campaign = getById({ id });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    const toggleMobileDrawer = useCallback(() => {
        setMobileDrawerOpen(prev => !prev);
    }, []);

    const t = useCommonTranslation();

    return (
        <>
            {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1,
                            borderBottom: '1px solid',
                            borderColor: 'grey.300',
                            backgroundColor: 'background.paper',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                        }}
                    >
                        <IconButton onClick={toggleMobileDrawer} edge="start" aria-label="menu">
                            <MenuIcon />
                        </IconButton>
                    </Box>
                    <Sidebar
                        id={id}
                        mobileDrawerOpen={mobileDrawerOpen}
                        toggleMobileDrawer={toggleMobileDrawer}
                    />
                    <Box sx={{ flexGrow: 1, px: 2, pt: 2 }}>
                        <CampaignOverviewPopShotsParentTab
                            id={id}
                            hasPopShots={campaign.popShotsCount > 0}
                        />
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: 'flex' }}>
                    <Sidebar id={id} />
                    <Box component="main" sx={{ flexGrow: 1, pt: 2, px: 2 }}>
                        <CampaignOverviewPopShotsParentTab
                            id={id}
                            hasPopShots={campaign.popShotsCount > 0}
                        />
                    </Box>
                </Box>
            )}
        </>
    );
};

const PageBreadcrumbs: SystemSearchEntityResource<Campaign>['renderEntityBreadcrumbs'] = () => (
    <Box display="flex" />
);

const setup: PageEntityConfig<Campaign> = {
    id: 'CampaignPopImagesPage',
    component: CampaignPopImagesPage,
    systemSearch: {
        title: undefined,
        description: 'Modules.Main.Campaigns.Overview.description',
        icon: campaignIcon,
        type: 'entity',
        aliases: undefined,
        route: PostAuthRoutes.Campaigns.CampaignPopImages,
        saveAsRecentPage: true,
        prefix: 'SystemSearch.prefixes.campaign',
        prefixDescription: 'SystemSearch.prefixDescriptions.campaign',
        breadcrumbs: [
            {
                label: 'Modules.Main.Campaigns.List.metaTitle',
                link: PostAuthRoutes.Campaigns.CampaignList,
            },
        ],
        nameGen: entity => `${entity?.name}-${entity?.id}`,
        entityName: 'Campaign',
        permissions: [PermissionName.UniledportalCampaignRead],
        renderEntityBreadcrumbs: PageBreadcrumbs,
        defaultIncludes: [
            'agency',
            'market',
            'creatives',
            'client',
            'creatives.resolution',
            'creatives.file',
            'budgetSummary',
            'budgetSummary.lineItems',
            'budgetSummaries',
            'budgetSummaries.lineItems',
            'notices',
            'errors',
            'creativeThumbnails',
            'bookingSummaries',
            'popShotsCount',
            'missingDisplaysCount',
            'happydemics',
            'additionalContacts',
        ],
    },
};

export default setup;
